// src/components/templates/diary/DiaryMeetingListView.jsx
import React, { useState, useMemo } from 'react';
import { 
  Briefcase, Search, Plus, Calendar, Clock, MapPin, ChevronRight, 
  Trash2, Users, FileText, CheckSquare, Filter, SortDesc, SortAsc,
  Video, Building2, MoreVertical, Copy, Archive
} from 'lucide-react';
import { Button, Card, Badge, MeetingStatus, MinuteType } from './UIComponents';

// --- MEETING CARD COMPONENT ---
const MeetingCard = ({ meeting, onSelect, onDelete, onDuplicate, onArchive }) => {
  const [showMenu, setShowMenu] = useState(false);

  const meetingDate = new Date(meeting.date);
  const participantCount = (meeting.participants || []).length;
  const agendaCount = (meeting.agenda || []).length;
  const openTaskCount = (meeting.minutes || []).filter(m => m.type === MinuteType.TASK && !m.completed).length;
  const completedTaskCount = (meeting.minutes || []).filter(m => m.type === MinuteType.TASK && m.completed).length;
  const totalMinutes = (meeting.minutes || []).length;

  const statusStyles = {
    [MeetingStatus.ACTIVE]: { 
      bg: 'bg-green-50', 
      border: 'border-green-200', 
      text: 'text-green-700',
      ring: 'ring-2 ring-green-500 ring-offset-2'
    },
    [MeetingStatus.COMPLETED]: { 
      bg: 'bg-gray-50', 
      border: 'border-gray-200', 
      text: 'text-gray-500',
      ring: ''
    },
    [MeetingStatus.PLANNED]: { 
      bg: 'bg-indigo-50', 
      border: 'border-indigo-200', 
      text: 'text-indigo-700',
      ring: ''
    },
  };

  const style = statusStyles[meeting.status] || statusStyles[MeetingStatus.PLANNED];

  // Check if meeting is today
  const isToday = new Date().toDateString() === meetingDate.toDateString();
  // Check if meeting is in past
  const isPast = meetingDate < new Date(new Date().setHours(0,0,0,0)) && meeting.status !== MeetingStatus.COMPLETED;

  return (
    <div 
      className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-lg transition-all cursor-pointer group relative ${style.ring}`}
      onClick={() => onSelect?.(meeting)}
    >
      {/* Today indicator */}
      {isToday && meeting.status !== MeetingStatus.COMPLETED && (
        <div className="absolute -top-2 left-4 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full shadow">
          TODAY
        </div>
      )}

      {/* Overdue indicator */}
      {isPast && (
        <div className="absolute -top-2 left-4 px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full shadow">
          OVERDUE
        </div>
      )}

      <div className="flex items-start justify-between">
        <div className="flex gap-5">
          {/* Date Box */}
          <div className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center shrink-0 border-2 ${style.bg} ${style.border} ${style.text}`}>
            <span className="text-[10px] font-bold uppercase tracking-wide">
              {meetingDate.toLocaleString('default', { month: 'short' })}
            </span>
            <span className="text-2xl font-bold leading-none">
              {meetingDate.getDate()}
            </span>
            <span className="text-[9px] opacity-70">
              {meetingDate.toLocaleString('default', { weekday: 'short' })}
            </span>
          </div>

          <div className="min-w-0">
            <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors flex items-center gap-2 flex-wrap">
              <span className="truncate">{meeting.title}</span>
              <Badge color={
                meeting.status === MeetingStatus.PLANNED ? 'blue' :
                meeting.status === MeetingStatus.ACTIVE ? 'green' : 'gray'
              }>{meeting.status}</Badge>
              {meeting.recurrence && meeting.recurrence !== 'NONE' && (
                <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded font-medium">
                  {meeting.recurrence}
                </span>
              )}
            </h3>
            
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" /> 
                {meeting.time} 
                <span className="text-gray-400 ml-1">({meeting.durationMinutes}m)</span>
              </span>
              {meeting.location && (
                <span className="flex items-center truncate max-w-[200px]">
                  <MapPin className="w-4 h-4 mr-1 shrink-0" /> 
                  {meeting.location}
                </span>
              )}
              <span className="flex items-center text-gray-400">
                {meeting.type === 'IN_PERSON' ? (
                  <><Building2 className="w-4 h-4 mr-1" /> In Person</>
                ) : (
                  <><Video className="w-4 h-4 mr-1" /> Virtual</>
                )}
              </span>
            </div>

            {/* Stats Row */}
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              <span className="flex items-center px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                <Users className="w-3 h-3 mr-1" /> {participantCount} Participants
              </span>
              <span className="flex items-center px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                <FileText className="w-3 h-3 mr-1" /> {agendaCount} Agenda
              </span>
              {openTaskCount > 0 && (
                <span className="flex items-center px-2 py-1 bg-amber-100 rounded-full text-amber-700 font-medium">
                  <CheckSquare className="w-3 h-3 mr-1" /> {openTaskCount} Open Tasks
                </span>
              )}
              {completedTaskCount > 0 && (
                <span className="flex items-center px-2 py-1 bg-green-100 rounded-full text-green-700">
                  <CheckSquare className="w-3 h-3 mr-1" /> {completedTaskCount} Done
                </span>
              )}
              {totalMinutes > 0 && meeting.status === MeetingStatus.COMPLETED && (
                <span className="flex items-center px-2 py-1 bg-indigo-100 rounded-full text-indigo-700">
                  {totalMinutes} Minutes
                </span>
              )}
            </div>

            {/* Objectives Preview */}
            {meeting.objectives && meeting.objectives.length > 0 && (
              <div className="mt-3 text-xs text-gray-400 line-clamp-1">
                <span className="font-medium">Objectives:</span> {meeting.objectives.slice(0, 2).join(' • ')}
                {meeting.objectives.length > 2 && ` +${meeting.objectives.length - 2} more`}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-indigo-500 transition-colors" />
          
          {/* Actions Menu */}
          <div className="relative">
            <button 
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-2 text-gray-300 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setShowMenu(false); }} />
                <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-20">
                  {onDuplicate && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); onDuplicate(meeting); setShowMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <Copy className="w-4 h-4 mr-2" /> Duplicate
                    </button>
                  )}
                  {onArchive && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); onArchive(meeting.id); setShowMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <Archive className="w-4 h-4 mr-2" /> Archive
                    </button>
                  )}
                  <button 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      if (window.confirm(`Delete "${meeting.title}"?`)) {
                        onDelete?.(meeting.id); 
                      }
                      setShowMenu(false); 
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---
export const DiaryMeetingListView = ({ 
  meetings = [], 
  onCreateMeeting, 
  onSelectMeeting, 
  onDeleteMeeting,
  onDuplicateMeeting,
  onArchiveMeeting,
  customStyling 
}) => {
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);

  // Filtered and sorted meetings
  const filteredMeetings = useMemo(() => {
    return meetings
      .filter(m => {
        const matchesStatus = filterStatus === 'ALL' || m.status === filterStatus;
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          m.title.toLowerCase().includes(query) ||
          (m.location || '').toLowerCase().includes(query) ||
          m.date.includes(query) ||
          (m.objectives || []).some(o => o.toLowerCase().includes(query));
        return matchesStatus && matchesSearch;
      })
      .sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
      });
  }, [meetings, filterStatus, searchQuery, sortOrder]);

  // Stats
  const stats = useMemo(() => ({
    total: meetings.length,
    planned: meetings.filter(m => m.status === MeetingStatus.PLANNED).length,
    active: meetings.filter(m => m.status === MeetingStatus.ACTIVE).length,
    completed: meetings.filter(m => m.status === MeetingStatus.COMPLETED).length,
  }), [meetings]);

  const filterButtons = [
    { key: 'ALL', label: 'All', count: stats.total },
    { key: MeetingStatus.PLANNED, label: 'Planned', count: stats.planned },
    { key: MeetingStatus.ACTIVE, label: 'Active', count: stats.active },
    { key: MeetingStatus.COMPLETED, label: 'Completed', count: stats.completed },
  ];

  return (
    <div className="flex flex-col h-full bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="flex-1 overflow-y-auto p-6">
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="flex flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Briefcase className="w-6 h-6 mr-2" 
              style={{ color: customStyling.primaryColor }}
              />
              Meetings
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {stats.total} total • {stats.planned} planned • {stats.active} active
            </p>
          </div>
          
          <Button onClick={onCreateMeeting} className="shadow-lg shadow-indigo-200"
           style={{ backgroundColor: customStyling.primaryColor }}
          >
            <Plus className="w-4 h-4 mr-2" /> 
            <span className=" hidden sm:inline"> New Meeting </span>
          </Button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="p-4 border-b border-gray-200 bg-white shrink-0">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          {/* Search */}
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input 
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
              placeholder="Search by title, location, date, or objectives..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Filter Toggle (Mobile) */}
          <button
            className="lg:hidden flex items-center px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {filterStatus !== 'ALL' && <span className="ml-2 w-2 h-2 bg-indigo-500 rounded-full" />}
          </button>

          {/* Filter Buttons */}
          <div className={`flex flex-wrap gap-2 ${showFilters ? 'flex' : 'hidden lg:flex'}`}>
            {filterButtons.map(btn => (
              <button
                key={btn.key}
                onClick={() => setFilterStatus(btn.key)}
                style={{
                  backgroundColor: filterStatus === btn.key 
                    ? customStyling?.primaryColor 
                    : undefined
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                  filterStatus === btn.key 
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-200' 
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                {btn.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  filterStatus === btn.key ? 'bg-white/20' : 'bg-gray-100'
                }`}>
                  {btn.count}
                </span>
              </button>
            ))}
          </div>

          {/* Sort Toggle */}
          <button
            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
            className="flex items-center px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            title={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
          >
            {sortOrder === 'desc' ? (
              <><SortDesc className="w-4 h-4 mr-1" /> Newest</>
            ) : (
              <><SortAsc className="w-4 h-4 mr-1" /> Oldest</>
            )}
          </button>
        </div>
      </div>

      {/* List Content */}
        <div className="max-w-5xl mx-auto space-y-4">
          {filteredMeetings.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {searchQuery || filterStatus !== 'ALL' ? (
                  <Search className="w-10 h-10 text-gray-300" />
                ) : (
                  <Briefcase className="w-10 h-10 text-gray-300" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">
                {searchQuery || filterStatus !== 'ALL' ? 'No matches found' : 'No meetings yet'}
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                {searchQuery || filterStatus !== 'ALL' 
                  ? 'Try adjusting your search or filters'
                  : 'Create your first meeting to get started'
                }
              </p>
              {!searchQuery && filterStatus === 'ALL' && (
                <Button onClick={onCreateMeeting}
                style={{ backgroundColor: customStyling.primaryColor }}
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Meeting
                </Button>
              )}
              {(searchQuery || filterStatus !== 'ALL') && (
                <button
                  onClick={() => { setSearchQuery(''); setFilterStatus('ALL'); }}
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Results count */}
              <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                <span>Showing {filteredMeetings.length} of {meetings.length} meetings</span>
              </div>

              {/* Meeting Cards */}
              {filteredMeetings.map(meeting => (
                <MeetingCard 
                  key={meeting.id} 
                  meeting={meeting}
                  onSelect={onSelectMeeting}
                  onDelete={onDeleteMeeting}
                  onDuplicate={onDuplicateMeeting}
                  onArchive={onArchiveMeeting}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiaryMeetingListView;